import PageHeader from "../Components/PageHeader";
import Section from "../Components/Section";
import { projectsData } from "../Data/Data";
import useChangeTitle from "../Hooks/useChangeTitle.jsx";

export default function Projects() {
  useChangeTitle("Our Projects");

  return (
    <>
      <PageHeader heading="Our Projects" />
      
      {/* Hero Section with Parallax Effect */}
      <div className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/home/c2.jpg" 
            alt="Rakan EG Projects" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 via-blue-800/70 to-blue-700/80"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto">
          <div className="animate-fade-in">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">
              Excellence in 
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
                Every Project
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-blue-100 max-w-4xl mx-auto leading-relaxed">
              Discover our comprehensive portfolio of successful electromechanical projects across Egypt, 
              from large-scale construction to precision maintenance services.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-4 rounded-full font-semibold text-lg transform hover:scale-105 transition-all duration-300 shadow-xl">
                View Construction Projects
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-blue-800 px-8 py-4 rounded-full font-semibold text-lg transform hover:scale-105 transition-all duration-300">
                Browse Maintenance Projects
              </button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent"></div>
      </div>

      {/* Construction Projects with Enhanced Design */}
      <Section 
        title={projectsData.construction.title}
        desc="Major construction projects featuring complete MEP installations and infrastructure development"
        className="py-24 bg-gradient-to-br from-white via-blue-50 to-white relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-32 h-32 bg-blue-100 rounded-full -translate-x-16 -translate-y-16 opacity-50"></div>
        <div className="absolute bottom-0 right-0 w-48 h-48 bg-blue-200 rounded-full translate-x-24 translate-y-24 opacity-30"></div>
        
        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-12">
          {projectsData.construction.projects.map((project, index) => (
            <div key={index} className="group relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-2xl blur opacity-0 group-hover:opacity-25 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border border-gray-100 overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-blue-600"></div>
                <div className="p-8">
                  <div className="flex items-center mb-4">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3 animate-pulse"></div>
                    <span className="text-sm text-green-600 font-semibold">COMPLETED</span>
                  </div>
                  <h3 className="font-bold text-xl mb-4 text-gray-800 group-hover:text-blue-600 transition-colors">
                    {project.name}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{project.services}</p>
                  <div className="flex flex-wrap gap-2">
                    {project.services.split(', ').map((service, idx) => (
                      <span 
                        key={idx} 
                        className="px-4 py-2 bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700 rounded-full text-sm font-medium hover:from-blue-200 hover:to-blue-300 transition-colors cursor-default"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="absolute bottom-0 right-0 w-20 h-20 bg-blue-50 rounded-tl-full opacity-50"></div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Project Gallery */}
      <Section 
        title="Project Gallery" 
        className="py-16 bg-gray-50"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          <div className="relative overflow-hidden rounded-lg shadow-lg group">
            <img 
              src="/images/home/hvac.jpg" 
              alt="Samsung Warehouses" 
              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-3 left-3 text-white">
              <h4 className="font-semibold text-sm">Samsung Warehouses</h4>
              <p className="text-xs">Beni Sweef</p>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-lg shadow-lg group">
            <img 
              src="/images/home/electrical-systems.jpg" 
              alt="Mivida Mall" 
              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-3 left-3 text-white">
              <h4 className="font-semibold text-sm">Mivida Mall</h4>
              <p className="text-xs">MEP Systems</p>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-lg shadow-lg group">
            <img 
              src="/images/home/decor home.jpg" 
              alt="Trigger Cafe" 
              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-3 left-3 text-white">
              <h4 className="font-semibold text-sm">Trigger Cafe</h4>
              <p className="text-xs">River Walk Mall</p>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-lg shadow-lg group">
            <img 
              src="/images/home/Light-Current-Systems.jpg" 
              alt="Masr Italia Mall" 
              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-3 left-3 text-white">
              <h4 className="font-semibold text-sm">Masr Italia Mall</h4>
              <p className="text-xs">October & Shorouk</p>
            </div>
          </div>
        </div>
      </Section>

      {/* Maintenance Projects with Modern Design */}
      <Section 
        title={projectsData.maintenance.title}
        desc="Ongoing maintenance contracts ensuring optimal performance of electromechanical systems"
        className="py-24 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white relative overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-2 h-32 bg-blue-400 opacity-20"></div>
          <div className="absolute top-40 right-20 w-2 h-24 bg-blue-500 opacity-30"></div>
          <div className="absolute bottom-20 left-32 w-2 h-28 bg-blue-300 opacity-25"></div>
        </div>
        
        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
          {projectsData.maintenance.projects.map((project, index) => (
            <div key={index} className="group relative">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-500 transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-4 h-4 bg-green-400 rounded-full mr-3 animate-pulse"></div>
                  <span className="text-green-300 text-sm font-semibold">ACTIVE CONTRACT</span>
                </div>
                <h3 className="font-bold text-lg mb-3 text-white group-hover:text-blue-300 transition-colors">
                  {project}
                </h3>
                <p className="text-gray-300 text-sm mb-4">Ongoing maintenance and support services</p>
                <div className="w-full h-1 bg-gradient-to-r from-blue-400 to-green-400 rounded opacity-70"></div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Services Overview */}
      <Section 
        title="Project Services" 
        desc="Comprehensive range of services delivered across all our projects"
        className="py-16 bg-gray-50"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mt-8">
          <div className="text-center">
            <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
              <img src="/images/services/hvac_231473096-1024x683.jpg" alt="HVAC" className="w-8 h-8 rounded-full object-cover" />
            </div>
            <h4 className="font-semibold text-sm">HVAC</h4>
          </div>
          <div className="text-center">
            <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
              <img src="/images/services/Plumbing-624x443.png" alt="Plumbing" className="w-8 h-8 rounded-full object-cover" />
            </div>
            <h4 className="font-semibold text-sm">Plumbing</h4>
          </div>
          <div className="text-center">
            <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
              <img src="/images/services/electrical.jpeg" alt="Electrical" className="w-8 h-8 rounded-full object-cover" />
            </div>
            <h4 className="font-semibold text-sm">Electrical</h4>
          </div>
          <div className="text-center">
            <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
              <img src="/images/services/light current.jpg" alt="Light Current" className="w-8 h-8 rounded-full object-cover" />
            </div>
            <h4 className="font-semibold text-sm">Light Current</h4>
          </div>
          <div className="text-center">
            <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3">
              <img src="/images/services/decoration.jpg" alt="Decoration" className="w-8 h-8 rounded-full object-cover" />
            </div>
            <h4 className="font-semibold text-sm">Decoration</h4>
          </div>
        </div>
      </Section>
    </>
  );
} 